import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Save, Check, Edit2, CircleMinus, CircleCheck, Lock } from "lucide-react";
import FormControl from "../../common/FormControl";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import StatusSwitch from "../../common/StatusSwitch";
import Pagination from "../../common/Pagination";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import Toast, { type ToastMessage } from "../../common/Toast";
import ApprovalDot from "../../common/ApprovalDot";
import WorkflowModal from "../../common/WorkflowModal";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedInput } from "../../../validation/ValidatedFields";
import { isUniqueLocal } from "../../../validation/uniqueness";
import {
    listLeaveTypes, createLeaveType, updateLeaveType, setLeaveTypeActive,
    getWeeklyOff, setWeeklyOff, type LeaveTypeStatus,
} from "../../../services/hrService";
import { LeaveType } from "../../../services/hrTypes";

const RBAC_ACTIVITY_TYPES = "HR_LEAVE_TYPES";

// 0=Monday … 6=Sunday (per WeeklyOffRule contract)
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const LeaveRules: React.FC = () => {
    // Leave Types is a maker-checker master gated on HR_LEAVE_TYPES.
    const viewPerm = usePermission(RBAC_ACTIVITY_TYPES, "view");
    const modifyPerm = usePermission(RBAC_ACTIVITY_TYPES, "view+modify");
    const canView = viewPerm.allowed;
    const canModify = modifyPerm.allowed;
    const permLoading = viewPerm.loading;

    const [types, setTypes] = useState<LeaveType[]>([]);
    const [statusFilter, setStatusFilter] = useState<LeaveTypeStatus>("active");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [weekdays, setWeekdays] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingRule, setSavingRule] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [editing, setEditing] = useState<LeaveType | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isFieldEditMode, setIsFieldEditMode] = useState(false);
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [quota, setQuota] = useState(0);
    const [isPaid, setIsPaid] = useState(true);
    const [isActive, setIsActive] = useState(true);
    const [togglingActive, setTogglingActive] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [workflowOpenFor, setWorkflowOpenFor] = useState<LeaveType | null>(null);
    const vh = useFieldValidation();

    const fetchAll = useCallback(async () => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const [t, rule] = await Promise.all([listLeaveTypes(statusFilter), getWeeklyOff()]);
            setTypes(t);
            setWeekdays(rule.weekdays || []);
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, [canView, statusFilter]);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useEffect(() => { setPage(1); }, [statusFilter, pageSize]);

    const paginatedTypes = useMemo(
        () => types.slice((page - 1) * pageSize, page * pageSize),
        [types, page, pageSize]
    );

    const openCreate = () => {
        setEditing(null); setName(""); setCode(""); setQuota(0); setIsPaid(true); setIsActive(true);
        setIsFieldEditMode(true);   // create form is editable from the start
        setModalError(null); vh.reset(); setIsOpen(true);
    };
    const openEdit = (t: LeaveType) => {
        setEditing(t); setName(t.name); setCode(t.code); setQuota(t.default_annual_quota); setIsPaid(t.is_paid);
        setIsActive(t.is_active !== 0);
        setIsFieldEditMode(false);  // open read-only — "Edit" unlocks the fields
        setModalError(null); vh.reset(); setIsOpen(true);
    };

    // Active/Inactive lifecycle toggle (is_active 1↔0) — fires immediately, independent of field edits.
    const handleToggleActive = async (next: boolean) => {
        if (!editing) return;
        setTogglingActive(true);
        try {
            const updated = await setLeaveTypeActive(editing.id, next);
            setIsActive(next);
            setEditing(updated);
            setToast({ type: "success", message: next ? "Leave type reactivated" : "Leave type inactivated" });
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Status change failed" });
        } finally {
            setTogglingActive(false);
        }
    };

    // Approved state — determines if form is locked
    const isApproved = editing?.workflow_state === "approved";
    const fieldsDisabled = !!editing && (!canModify || isApproved || !isFieldEditMode);

    const handleSave = async () => {
        setModalError(null);
        const ok = vh.validateAll([
            { name: "name", code: 109, value: name, required: true },
            { name: "code", code: 109, value: code, required: true },
            { name: "quota", code: 206, value: String(quota), required: false },
        ]);
        // Inline uniqueness pre-check (backend 409 is the hard gate).
        let uniqOk = true;
        if (name.trim() && !isUniqueLocal(952, name, types, editing?.id)) {
            vh.setError("name", "A leave type with this name already exists"); uniqOk = false;
        }
        if (code.trim() && !isUniqueLocal(953, code, types, editing?.id)) {
            vh.setError("code", "A leave type with this code already exists"); uniqOk = false;
        }
        if (!ok || !uniqOk) return;
        if (!name.trim() || !code.trim()) { setModalError("Name and code are required."); return; }
        setSaving(true);
        try {
            const payload = { name: name.trim(), code: code.trim(), default_annual_quota: Number(quota) || 0, is_paid: isPaid };
            if (editing) await updateLeaveType(editing.id, payload);
            else await createLeaveType(payload as any);
            setToast({ type: "success", message: editing ? "Leave type updated" : "Leave type created" });
            setIsOpen(false);
            fetchAll();
        } catch (err: any) {
            setModalError(err.response?.data?.detail || err.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggleDay = (d: number) => {
        setWeekdays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)));
    };

    const handleSaveRule = async () => {
        setSavingRule(true);
        try {
            const rule = await setWeeklyOff(weekdays);
            setWeekdays(rule.weekdays || []);
            setToast({ type: "success", message: "Weekly-off rule saved" });
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Save failed" });
        } finally {
            setSavingRule(false);
        }
    };

    // Workflow (submit/approve/reject/reopen) is driven end-to-end by the unified
    // <WorkflowModal> via the /api/v1/wf engine (kind="hr_leave_type").
    const closeWf = () => setWorkflowOpenFor(null);

    // ── Not-authorised gate ────────────────────────────────────────────────────
    if (!permLoading && !canView) {
        return (
            <div className="netbody-card flex flex-col h-full">
                <NoDataFound title="Not authorised" message={viewPerm.message || NOT_AUTHORISED_MSG} />
            </div>
        );
    }

    const headerText = editing
        ? isApproved ? <>Viewing (locked) "<span className="item-highlight">{editing.name}</span>"</>
        : (canModify && isFieldEditMode) ? <>Editing "<span className="item-highlight">{editing.name}</span>"</>
        : <>Viewing "<span className="item-highlight">{editing.name}</span>"</>
        : 'Create Leave Type';

    // Footer button visibility + required-field guard (modal-design conventions).
    const isInvalid = !name.trim() || !code.trim();
    const showCreateBtn = !editing && canModify;
    const showEditActions = !!editing && canModify && !isApproved;

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter === "inactive" ? "INACTIVE" : "ACTIVE"}
                    onChange={(next) => setStatusFilter(next === "INACTIVE" ? "inactive" : "active")}
                    noun="leave types"
                    testId="SEL_leave_type_status"
                />
                <div className="ml-auto">
                    {canModify && <Button variant="fill" icon={Plus} onClick={openCreate} data-testid="BTN_leave_type_create">Leave Type</Button>}
                </div>
            </div>

            <div className="netbody-content" style={{ overflowY: "auto" }}>
                {/* Leave types table */}
                <div className="table-list" style={{ height: "auto", minHeight: 0, flex: "0 0 auto" }}>
                    <div className="table-list-scroll" style={{ flex: "0 0 auto", overflow: "visible" }}>
                        <table className="table-list-table">
                            <thead>
                                <tr>
                                    <th className="table-list-th" style={{ width: 34 }} aria-label="Workflow status" />
                                    <th className="table-list-th table-list-cell--sno">S.No</th>
                                    <th className="table-list-th">Code</th>
                                    <th className="table-list-th">Name</th>
                                    <th className="table-list-th">Annual Quota</th>
                                    <th className="table-list-th">Paid</th>
                                    <th className="table-list-th table-list-cell--status">Status</th>
                                </tr>
                            </thead>
                            <tbody aria-busy={loading} aria-live="polite">
                                {loading && <Skeleton.TableRows cols={["pill", "3xs", "sm", "md", "xs", "pill", "pill"]} />}
                                {!loading && paginatedTypes.map((t, idx) => (
                                    <tr key={t.id} className="table-list-row" onClick={() => openEdit(t)} data-testid={`ROW_leave_type_${t.id}`}>
                                        <td className="table-list-cell" style={{ width: 34 }} data-testid={`DOT_leave_type_wf_${t.id}`}>
                                            <ApprovalDot
                                                state={(t.workflow_state || "draft") as any}
                                                onClick={(e) => { e.stopPropagation(); setWorkflowOpenFor(t); }}
                                                title="Workflow & history"
                                            />
                                        </td>
                                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                        <td className="table-list-cell">{t.code}</td>
                                        <td className="table-list-cell"><span className="table-list-cell-title cell-name-lock">{t.workflow_state === 'approved' && (<Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />)}{t.name}</span></td>
                                        <td className="table-list-cell">{t.default_annual_quota}</td>
                                        <td className="table-list-cell">
                                            <span className={`status-badge xs ${t.is_paid ? "success" : "neutral"}`}>{t.is_paid ? "Paid" : "Unpaid"}</span>
                                        </td>
                                        <td className="table-list-cell table-list-cell--status">
                                            <span className={`status-badge xs ${t.is_active === 0 ? "danger" : "success"}`}>{t.is_active === 0 ? "Inactive" : "Active"}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {!loading && types.length === 0 && (
                        <NoDataFound
                            title={statusFilter === "inactive" ? "No inactive leave types" : "No leave types"}
                            message={statusFilter === "inactive" ? "No leave types are currently inactive." : "Create a leave type to begin."}
                        />
                    )}
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalRecords={types.length}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={[10, 25, 50, 100]}
                        recordLabel="leave types"
                    />
                </div>

                {/* Weekly-off rule editor */}
                <div style={{ padding: "1rem 1.25rem 1.25rem", border: "1px solid var(--border-subtle)", borderRadius: 10, marginTop: "1.25rem" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 }}>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--fg-primary, #1a1a2e)" }}>Weekly-off Rule</div>
                            <div style={{ fontSize: "0.75rem", color: "var(--fg-muted, #6b7280)", marginTop: 2 }}>Select the days that are weekly off for your organisation</div>
                        </div>
                        <Button
                            variant="fill"
                            icon={Save}
                            disabled={!canModify || savingRule}
                            title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                            onClick={handleSaveRule}
                            data-testid="BTN_weekly_off_save"
                        >
                            {savingRule ? "Saving..." : "Save Rule"}
                        </Button>
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {WEEKDAYS.map((label, idx) => {
                            const isOff = weekdays.includes(idx);
                            return (
                                <button
                                    key={idx}
                                    type="button"
                                    disabled={!canModify}
                                    onClick={() => toggleDay(idx)}
                                    data-testid={`CHK_weekly_off_${idx}`}
                                    style={{
                                        width: 52,
                                        height: 44,
                                        borderRadius: 10,
                                        border: isOff ? "2px solid #4f1b8c" : "1.5px solid var(--border-subtle, #e2e8f0)",
                                        background: isOff ? "#4f1b8c" : "var(--bg-surface, #f8fafc)",
                                        color: isOff ? "#fff" : "var(--fg-secondary, #374151)",
                                        fontWeight: isOff ? 700 : 500,
                                        fontSize: "0.8125rem",
                                        cursor: canModify ? "pointer" : "not-allowed",
                                        opacity: !canModify ? 0.55 : 1,
                                        transition: "all 0.15s ease",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 3,
                                        boxShadow: isOff ? "0 2px 8px rgba(79,27,140,0.25)" : "none",
                                    }}
                                >
                                    <span style={{ letterSpacing: 0.3 }}>{label}</span>
                                    {isOff && (
                                        <span style={{
                                            fontSize: "0.55rem",
                                            letterSpacing: 0.8,
                                            background: "rgba(255,255,255,0.22)",
                                            borderRadius: 3,
                                            padding: "1px 4px",
                                            fontWeight: 700,
                                        }}>
                                            OFF
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm" data-testid="MOD_leave_type">
                <Modal.Header>{headerText}</Modal.Header>
                <Modal.Body>
                    {isApproved && (
                        <div className="approved-banner">
                            <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                        </div>
                    )}
                    {modalError && <div className="form-error" style={{ marginBottom: 8 }}>{modalError}</div>}
                    <form id="leave-type-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="flex flex-col gap-y-4">
                        <ValidatedInput vh={vh} name="name" code={109} required label="Name" disabled={fieldsDisabled} value={name} onChange={(e) => setName(e.target.value)} onAfterBlur={(v) => { if (v.trim() && !isUniqueLocal(952, v, types, editing?.id)) vh.setError("name", "A leave type with this name already exists"); }} data-testid="INP_leave_type_name" />
                        <ValidatedInput vh={vh} name="code" code={109} required label="Code" disabled={fieldsDisabled} value={code} onChange={(e) => setCode(e.target.value)} onAfterBlur={(v) => { if (v.trim() && !isUniqueLocal(953, v, types, editing?.id)) vh.setError("code", "A leave type with this code already exists"); }} data-testid="INP_leave_type_code" />
                        <ValidatedInput vh={vh} name="quota" code={206} label="Default Annual Quota" type="number" disabled={fieldsDisabled} value={String(quota)} onChange={(e) => setQuota(Number(e.target.value))} data-testid="INP_leave_type_quota" />
                        <FormControl.Checkbox label="Paid leave" checked={isPaid} disabled={fieldsDisabled} onChange={(c) => setIsPaid(c)} data-testid="CHK_leave_type_paid" />
                    </form>
                </Modal.Body>
                {(showCreateBtn || (editing && canModify)) && (
                    <Modal.Footer className={editing ? "app-modal-footer--split" : ""}>
                        {/* Bottom-left: Active/Inactive lifecycle toggle (existing record), but not if approved */}
                        {editing && canModify && !isApproved && (
                            <Button
                                variant={isActive ? "inactive-outline" : "active-outline"}
                                icon={isActive ? CircleMinus : CircleCheck}
                                onClick={() => handleToggleActive(!isActive)}
                                disabled={togglingActive || saving}
                                data-testid="TGL_leave_type_active"
                            >
                                {togglingActive ? "..." : isActive ? "Inactive" : "Active"}
                            </Button>
                        )}
                        {showCreateBtn && (
                            <Button variant="fill" icon={Save} onClick={handleSave} disabled={isInvalid || saving} data-testid="BTN_leave_type_save">
                                Submit
                            </Button>
                        )}
                        {showEditActions && (
                            isFieldEditMode ? (
                                <Button variant="update" icon={Check} onClick={handleSave} disabled={saving} data-testid="BTN_leave_type_save">
                                    Update
                                </Button>
                            ) : (
                                <Button variant="fill" icon={Edit2} onClick={() => setIsFieldEditMode(true)} data-testid="BTN_leave_type_edit_unlock">
                                    Edit
                                </Button>
                            )
                        )}
                    </Modal.Footer>
                )}
            </Modal>

            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={closeWf}
                kind="hr_leave_type"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={fetchAll}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default LeaveRules;
